import clsx from 'clsx'
import { differenceInDays } from 'date-fns'
import { Button, Typography } from 'gu-ui-kit'
import { ReactComponent as PicSVG } from 'images/banners/wallets/pic.svg'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styles from 'modules/Finance/Wallets/subcomponents/Banner/Banner.module.scss'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import formatMoney from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { useSelector } from 'react-redux'

export const Banner = ({ className = '' }) => {
  const pathGen = useGeneratePathI18n()
  const breakpoint = useBreakpoint()
  const { takenPromisedPayment: { data: takenPromisedPayment = [], loaded } = {} } = useSelector(
    (state) => state['finance/promised-payment'],
  )
  const { activeWalletID } = useSelector((state) => state['finance/wallets'])
  const { t } = useTranslation()

  const currentWalletPromisedPayment = useMemo(() => {
    if (activeWalletID !== null && loaded && takenPromisedPayment.length) {
      return [...takenPromisedPayment]
        .sort((a, b) => a.created_at >= b.created_at)
        .find(
          ({ status, wallet, repayment_date }) =>
            status === 'approved' && wallet?.id === activeWalletID && repayment_date !== null,
        )
    }

    return null
  }, [takenPromisedPayment, loaded, activeWalletID])

  const warnStatus = useMemo(() => {
    if (currentWalletPromisedPayment?.repayment_date) {
      const difference = differenceInDays(
        new Date(currentWalletPromisedPayment.repayment_date * 1000),
        new Date(),
      )

      switch (true) {
        case difference > 5 && difference < 30:
          return 'warning'
        case difference < 5:
          return 'error'
        case difference > 30:
          return 'info'
      }
    }
  }, [currentWalletPromisedPayment])

  const { remaining_amount, wallet } = useMemo(
    () => currentWalletPromisedPayment || {},
    [currentWalletPromisedPayment],
  )

  if (!currentWalletPromisedPayment) {
    return null
  }

  return (
    <div className={clsx(styles['banner'], className)}>
      {breakpoint.sm && (
        <div className={styles['banner__fig']}>
          <PicSVG />
        </div>
      )}
      <div className={clsx(styles['banner__content'], styles['banner__content_' + warnStatus])}>
        <Typography
          variant={'Body-Accent-30'}
          color={'Label-Onsurface'}
          component={'div'}
          className={styles['banner__content-header']}
        >
          {t('wallets::banner.heading')}
        </Typography>
        <div className={'margin-bottom-24'}>
          <Typography
            variant={breakpoint.xl ? 'Body-Accent-80' : 'Body-Accent-60'}
            color={'Label-Onsurface'}
            component={'span'}
            noWrap
          >
            <span
              dangerouslySetInnerHTML={{
                __html: formatMoney(remaining_amount, wallet.currency),
              }}
            />
          </Typography>
        </div>
        <div className={styles['banner__foot']}>
          <Button
            to={pathGen(ROUTES.BILLING_TOP_UP)}
            component={Link}
            variant={'primary'}
            size={'m'}
            bgColor={'Background-Primary'}
            color={'Label-Primary'}
            bgColorHover={'Background-Seven'}
          >
            {t('common::buttons.pay')}
          </Button>
        </div>
      </div>
    </div>
  )
}

Banner.propTypes = {
  className: PropTypes.string,
}
